import { HABIT_CATEGORY_ORDER } from '@/lib/constants/habitBadges';

interface ScorableQuestion {
  id: string;
  category?: string;
  options: { id: string; score: number }[];
}

export interface PillarScore {
  category: string;
  score: number;
  max: number;
  percentage: number;
  answered: number;
  total: number;
}

const percent = (score: number, max: number) => (max > 0 ? Math.round((score / max) * 100) : 0);

/**
 * Performance on each lifestyle pillar, plus overall, from the answers in
 * hand (questionId → optionId). Same rule as the saved total: a question's
 * maximum is its best option and counts whether or not it was answered, so
 * the pillars add up to the overall exactly. Pillars follow the habit order;
 * unknown categories come after, then questions with no category as "Other".
 */
export function pillarScores(questions: ScorableQuestion[], answers: Record<string, string | undefined>) {
  const byCategory = new Map<string, PillarScore>();
  for (const question of questions) {
    const category = question.category || 'Other';
    const pillar = byCategory.get(category) ?? { category, score: 0, max: 0, percentage: 0, answered: 0, total: 0 };
    pillar.total += 1;
    pillar.max += question.options.length ? Math.max(...question.options.map((o) => o.score)) : 0;
    const chosen = question.options.find((o) => o.id === answers[question.id]);
    if (chosen) {
      pillar.score += chosen.score;
      pillar.answered += 1;
    }
    byCategory.set(category, pillar);
  }

  const known = HABIT_CATEGORY_ORDER as string[];
  const rank = (c: string) => (known.includes(c) ? known.indexOf(c) : c === 'Other' ? known.length + 1 : known.length);
  const pillars = [...byCategory.values()]
    .sort((a, b) => rank(a.category) - rank(b.category))
    .map((p) => ({ ...p, percentage: percent(p.score, p.max) }));

  const score = pillars.reduce((sum, p) => sum + p.score, 0);
  const max = pillars.reduce((sum, p) => sum + p.max, 0);
  return { pillars, overall: { score, max, percentage: percent(score, max) } };
}
